<template>
  <v-dialog :value="value" fullscreen hide-overlay transition="fade-transition" @input="onDialogInput">
    <div class="add-media-from-url-dialog" @click.self="cancel">
      <div class="add-media-from-url-dialog__panel" role="dialog" :aria-labelledby="titleId" @click.stop>
        <div class="add-media-from-url-dialog__topbar">
          <h2 :id="titleId" class="add-media-from-url-dialog__title">{{ modalTitle }}</h2>
          <v-btn icon title="Close" @click="cancel">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="add-media-from-url-dialog__form">
          <input
            ref="urlInput"
            v-model.trim="urlInput"
            type="url"
            class="add-media-from-url-dialog__input"
            :placeholder="inputPlaceholder"
            autocomplete="off"
            @keydown.enter.prevent="addRow"
          />
          <v-btn
            color="primary"
            depressed
            class="add-media-from-url-dialog__add-btn"
            :disabled="!canAddMorePreviewRows"
            @click="addRow"
          >
            Add
          </v-btn>
        </div>

        <div v-if="rows.length" class="add-media-from-url-dialog__previews" role="list" aria-label="URL media previews">
          <div v-for="row in rows" :key="row.id" class="add-media-from-url-dialog__tile" role="listitem">
            <button
              type="button"
              class="add-media-from-url-dialog__thumb"
              :aria-label="thumbAriaLabel(row)"
              :disabled="row.loaded !== true"
              @click="openRowPreview(row)"
            >
              <img
                v-if="row.mediaKind === 'image'"
                class="add-media-from-url-dialog__thumb-media add-media-from-url-dialog__thumb-media--fill"
                :src="row.url"
                alt=""
                @load="onImageLoad(row)"
                @error="onProbeError(row)"
              />
              <div v-else class="add-media-from-url-dialog__thumb-video-wrap">
                <video
                  class="add-media-from-url-dialog__thumb-media add-media-from-url-dialog__thumb-media--video"
                  :src="row.url"
                  muted
                  playsinline
                  preload="metadata"
                  @loadeddata="onVideoReady(row)"
                  @error="onProbeError(row)"
                />
                <v-icon
                  v-if="row.loaded === true"
                  class="add-media-from-url-dialog__thumb-video-icon"
                  size="22"
                  color="white"
                >
                  mdi-play
                </v-icon>
              </div>
              <div v-if="row.loaded === null" class="add-media-from-url-dialog__thumb-overlay" aria-busy="true">
                <v-progress-circular indeterminate size="18" width="2" color="white" />
              </div>
            </button>
            <button
              type="button"
              class="add-media-from-url-dialog__remove"
              aria-label="Remove from list"
              @click.stop="removeRow(row.id)"
            >
              <v-icon size="14">mdi-close</v-icon>
            </button>
          </div>
        </div>

        <p v-if="!canAddMorePreviewRows" class="add-media-from-url-dialog__hint">
          Maximum {{ maxAttachments }} attachments per message.
        </p>

        <div class="add-media-from-url-dialog__actions">
          <v-btn text class="add-media-from-url-dialog__action-btn" @click="cancel">Cancel</v-btn>
          <v-btn
            color="primary"
            depressed
            class="add-media-from-url-dialog__action-btn"
            :disabled="!hasSavable"
            @click="save"
          >
            Save
          </v-btn>
        </div>
      </div>
    </div>

    <PreviewImage
      v-if="rowPreview.src && !rowPreview.isVideo"
      :src="rowPreview.src"
      :show-preview="rowPreview.show"
      @close="closeRowPreview"
    />
    <PreviewVideo
      v-if="rowPreview.src && rowPreview.isVideo"
      :src="rowPreview.src"
      :show-preview="rowPreview.show"
      @close="closeRowPreview"
    />
  </v-dialog>
</template>

<script>
import PreviewImage from './PreviewImage.vue';
import PreviewVideo from './PreviewVideo.vue';
import { isAllowedImageUrl, isVideoUrl } from './utils';

function newRowId() {
  return `url-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2, 10)}`;
}

export default {
  name: 'AddMediaFromUrlDialog',
  components: { PreviewImage, PreviewVideo },
  props: {
    value: { type: Boolean, default: false },
    maxAttachments: { type: Number, default: 1 },
    attachmentsCount: { type: Number, default: 0 },
    /** `image` — only images; `video` — only videos; `all` — try image first unless URL looks like a video file. */
    mediaTypes: {
      type: String,
      default: 'all',
      validator: v => ['image', 'video', 'all'].includes(v),
    },
  },

  data() {
    return {
      urlInput: '',
      rows: [],
      rowPreview: {
        show: false,
        src: '',
        isVideo: false,
      },
      titleId: 'add-media-from-url-title',
    };
  },

  computed: {
    allowImage() {
      return this.mediaTypes === 'image' || this.mediaTypes === 'all';
    },
    allowVideo() {
      return this.mediaTypes === 'video' || this.mediaTypes === 'all';
    },
    modalTitle() {
      if (this.mediaTypes === 'image') return 'Add images from URL';
      if (this.mediaTypes === 'video') return 'Add videos from URL';
      return 'Add photos and videos from URL';
    },
    inputPlaceholder() {
      if (this.mediaTypes === 'image') return 'Enter image URL';
      if (this.mediaTypes === 'video') return 'Enter video URL';
      return 'Enter image or video URL';
    },
    remainingSlots() {
      return Math.max(0, this.maxAttachments - this.attachmentsCount);
    },
    canAddMorePreviewRows() {
      return this.attachmentsCount + this.rows.length < this.maxAttachments;
    },
    readyCount() {
      return this.rows.filter(r => r && r.loaded === true).length;
    },
    hasSavable() {
      return this.readyCount > 0 && this.remainingSlots > 0;
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.resetState();
        this.$nextTick(() => {
          const el = this.$refs.urlInput;
          if (el && typeof el.focus === 'function') el.focus();
        });
      }
    },
  },

  methods: {
    thumbAriaLabel(row) {
      if (!row) return 'Media preview';
      if (row.loaded === true) return row.mediaKind === 'video' ? 'Preview video' : 'Preview image';
      return 'Loading preview';
    },

    notifyError(message) {
      this.$store.commit('setMessages', {
        type: 'error',
        messages: message,
      });
    },

    resetState() {
      this.urlInput = '';
      this.rows = [];
      this.closeRowPreview();
    },

    onDialogInput(val) {
      this.$emit('input', !!val);
      if (!val) this.resetState();
    },

    cancel() {
      this.$emit('input', false);
      this.resetState();
    },

    isValidHttpUrl(raw) {
      const s = String(raw || '').trim();
      if (!s) return false;
      try {
        const u = new URL(s);
        return u.protocol === 'http:' || u.protocol === 'https:';
      } catch {
        return false;
      }
    },

    initialMediaKindForUrl(urlStr) {
      if (this.mediaTypes === 'image') return 'image';
      if (this.mediaTypes === 'video') return 'video';
      return isVideoUrl(urlStr) ? 'video' : 'image';
    },

    addRow() {
      if (!this.canAddMorePreviewRows) {
        this.notifyError(`You can only attach up to ${this.maxAttachments} images per message.`);
        return;
      }
      const raw = String(this.urlInput || '').trim();
      if (!this.isValidHttpUrl(raw)) {
        this.notifyError('Enter a valid URL');
        return;
      }
      if (this.mediaTypes === 'image' && !isAllowedImageUrl(raw)) {
        this.notifyError('Only PNG, JPG, JPEG, and WebP image URLs are supported.');
        return;
      }
      this.rows.push({
        id: newRowId(),
        url: raw,
        loaded: null,
        mediaKind: this.initialMediaKindForUrl(raw),
        _flipped: false,
      });
      this.urlInput = '';
      this.$nextTick(() => {
        const el = this.$refs.urlInput;
        if (el && typeof el.focus === 'function') el.focus();
      });
    },

    onImageLoad(row) {
      if (!row || row.loaded !== null || row.mediaKind !== 'image') return;
      if (!this.rows.some(r => r.id === row.id)) return;
      row.loaded = true;
    },

    onVideoReady(row) {
      if (!row || row.loaded !== null || row.mediaKind !== 'video') return;
      if (!this.rows.some(r => r.id === row.id)) return;
      row.loaded = true;
    },

    loadFailedMessage() {
      if (this.mediaTypes === 'image') return "This image couldn't be loaded. Check the URL and try again.";
      if (this.mediaTypes === 'video') return "This video couldn't be loaded. Check the URL and try again.";
      return "This media couldn't be loaded. Check the URL and try again.";
    },

    onProbeError(row) {
      if (!row || row.loaded !== null) return;
      if (this.mediaTypes === 'all' && !row._flipped) {
        const canTryVideo = row.mediaKind === 'image' && this.allowVideo;
        const canTryImage = row.mediaKind === 'video' && this.allowImage;
        if (canTryVideo || canTryImage) {
          row._flipped = true;
          row.mediaKind = row.mediaKind === 'image' ? 'video' : 'image';
          return;
        }
      }
      const url = row.url;
      const idx = this.rows.findIndex(r => r.id === row.id);
      if (idx === -1) return;
      this.rows.splice(idx, 1);
      if (this.rowPreview.src === url && !this.rows.some(r => r.url === url)) {
        this.closeRowPreview();
      }
      this.notifyError(this.loadFailedMessage());
    },

    removeRow(id) {
      const row = this.rows.find(r => r.id === id);
      if (row && this.rowPreview.src === row.url) {
        const otherSameUrl = this.rows.some(r => r.id !== id && r.url === row.url);
        if (!otherSameUrl) this.closeRowPreview();
      }
      const idx = this.rows.findIndex(r => r.id === id);
      if (idx !== -1) this.rows.splice(idx, 1);
    },

    openRowPreview(row) {
      if (!row?.url || row.loaded !== true) return;
      this.rowPreview.src = row.url;
      this.rowPreview.isVideo = row.mediaKind === 'video';
      this.rowPreview.show = true;
    },

    closeRowPreview() {
      this.rowPreview.show = false;
      this.rowPreview.src = '';
      this.rowPreview.isVideo = false;
    },

    save() {
      const items = this.rows
        .filter(r => r && r.loaded === true)
        .map(r => ({ url: r.url, isVideo: r.mediaKind === 'video' }));
      if (!items.length) {
        this.notifyError('Add at least one URL that loads successfully.');
        return;
      }
      if (this.remainingSlots <= 0) {
        this.notifyError(`You can only attach up to ${this.maxAttachments} images per message.`);
        return;
      }
      this.$emit('save', { items });
      this.$emit('input', false);
      this.resetState();
    },
  },
};
</script>

<style scoped lang="scss">
.add-media-from-url-dialog {
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.add-media-from-url-dialog__panel {
  width: 100%;
  max-width: 500px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  padding: 16px 18px 18px;
  max-height: calc(100vh - 48px);
  overflow: auto;
}

.add-media-from-url-dialog__topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.add-media-from-url-dialog__title {
  margin: 4px 0 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--main-text-color, #1f1f1f);
}

.add-media-from-url-dialog__form {
  display: flex;
  align-items: stretch;
  gap: 10px;
}

.add-media-from-url-dialog__input {
  flex: 1 1 auto;
  min-width: 0;
  height: 40px;
  padding: 0 12px;
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: var(--v-primary-base, #1976d2);
  }

  &::placeholder {
    color: #9e9e9e;
  }
}

.add-media-from-url-dialog__add-btn {
  flex: 0 0 auto;
  height: 40px !important;
  border-radius: 8px !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}

.add-media-from-url-dialog__previews {
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 10px;
}

.add-media-from-url-dialog__tile {
  position: relative;
  flex: 0 0 auto;
}

.add-media-from-url-dialog__thumb {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #f6f6f6;
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  display: grid;
  place-items: center;

  &:disabled {
    cursor: default;
    opacity: 0.85;
  }
}

.add-media-from-url-dialog__thumb-media--fill {
  position: absolute;
  inset: 0;
  object-fit: cover;
}

.add-media-from-url-dialog__thumb-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.add-media-from-url-dialog__thumb-video-wrap {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1;
  }
}

.add-media-from-url-dialog__thumb-media--video {
  position: absolute;
  inset: 0;
  object-fit: cover;
}

.add-media-from-url-dialog__thumb-video-icon {
  position: relative;
  z-index: 2;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.45));
}

.add-media-from-url-dialog__thumb-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: grid;
  place-items: center;
  z-index: 3;
}

.add-media-from-url-dialog__remove {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
  z-index: 4;
  padding: 0;
}

.add-media-from-url-dialog__hint {
  margin: 14px 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.55);
}

.add-media-from-url-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
  padding-top: 4px;
}

.add-media-from-url-dialog__action-btn {
  min-width: 88px !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}
</style>
